/**
 * preferences/index — single dispatcher for the per-report-type
 * preferences sub-form.
 *
 * Both the New Report wizard (ReportPreferencesStep) and the Rerun
 * panels mount the same per-type form so the user gets the same
 * editing surface in both flows. Pulling the dispatch into one place
 * means a new report type only adds a row to ONE switch instead of
 * two; it also means the wizard and rerun can't drift on which
 * fields are editable.
 *
 * Re-exports the validators so callers don't have to know which
 * file each one lives in.
 */
import React from 'react';
import type { ReportTypeId } from '../../../config/reportTypes';
import type { TypedPreferencesByType } from '../../../types/siteRag';
import {
  FeasibilityPreferencesForm,
} from './FeasibilityPreferences';
import {
  EnvironmentalPreferencesForm,
} from './EnvironmentalPreferences';
import {
  TestFitPreferencesForm,
} from './TestFitPreferences';
import {
  PowerAvailabilityPreferencesForm,
} from './PowerAvailabilityPreferences';
import {
  WaterResourcePreferencesForm,
} from './WaterResourcePreferences';
import {
  InterconnectionPreferencesForm,
} from './InterconnectionPreferences';
import {
  CustomBriefPreferencesForm,
} from './CustomBriefPreferences';
import {
  ExecutiveSummaryPreferencesForm,
} from './ExecutiveSummaryPreferences';
import {
  FinancialPreferencesForm,
} from './FinancialPreferences';
import type { SiteEntity } from '../../../types/site';
import type { ValidationResult } from './shared';

export type PreferencesValueOf<T extends ReportTypeId> = TypedPreferencesByType[T];

interface PreferencesFormForProps<T extends ReportTypeId> {
  reportType: T;
  value: PreferencesValueOf<T>;
  onChange: (next: PreferencesValueOf<T>) => void;
  /**
   * SSOT — handler that opens the Site Project Intent editor. Wired by
   * callers (wizard / Site Config Panel / rerun) so that the SSOT
   * summary banner's "Edit Site Project Intent →" link can route the
   * user to the canonical editor. Optional: when omitted, the summary
   * banner renders without the inline link.
   *
   * Replaces the IC3 `siteIntent` prop pattern — post-SSOT there's no
   * inherited-mirror UI; the shared fields simply don't exist on the
   * per-report typed-prefs interfaces.
   */
  onOpenSiteIntent?: () => void;
  /** Custom Brief — site-scoped saved templates. */
  site?: Pick<SiteEntity, 'id' | 'savedCustomBriefs'>;
  onSaveCustomBrief?: (preferences: import('../../../types/siteRag').CustomBriefPreferences, existingId?: string) => Promise<string>;
  onDeleteCustomBrief?: (briefId: string) => Promise<void>;
  /** Executive Summary — selectable source reports at this site. */
  siteReports?: import('../../../types/siteRag').SiteRagReport[];
}

/** Render the per-type preferences sub-form. The wizard and the
 *  rerun panel both call this so they share the editing UX. */
export function PreferencesFormFor<T extends ReportTypeId>(
  props: PreferencesFormForProps<T>,
): React.ReactElement {
  const { reportType, value, onChange, onOpenSiteIntent, site, onSaveCustomBrief, onDeleteCustomBrief, siteReports } = props;
  // The per-type form components were authored before this dispatcher
  // existed and accept their own narrow value shape, so we cast the
  // generic `value` through `any`. The dispatcher's external surface
  // remains type-safe via `PreferencesFormForProps<T>`.
  // PB1 hotfix: fall back to `{}` so sub-forms never receive
  // `undefined` (which crashes any `value.X` read on first render
  // when no `typedPreferencesOverride` exists yet on a fresh site).
  const v = (value ?? {}) as any;
  const cb = onChange as any;
  switch (reportType) {
    case 'feasibility':
      return <FeasibilityPreferencesForm value={v} onChange={cb} onOpenSiteIntent={onOpenSiteIntent} />;
    case 'supply-chain-risk':
      return <FeasibilityPreferencesForm value={v} onChange={cb} onOpenSiteIntent={onOpenSiteIntent} />;
    case 'environmental':
      return <EnvironmentalPreferencesForm value={v} onChange={cb} />;
    case 'test-fit':
      return <TestFitPreferencesForm value={v} onChange={cb} />;
    case 'power-availability':
      return <PowerAvailabilityPreferencesForm value={v} onChange={cb} onOpenSiteIntent={onOpenSiteIntent} />;
    case 'water-resource':
      return <WaterResourcePreferencesForm value={v} onChange={cb} onOpenSiteIntent={onOpenSiteIntent} />;
    case 'interconnection':
      return <InterconnectionPreferencesForm value={v} onChange={cb} />;
    case 'custom-brief':
      return (
        <CustomBriefPreferencesForm
          value={v}
          onChange={cb}
          siteId={site?.id}
          savedBriefs={site?.savedCustomBriefs}
          onSaveToSite={onSaveCustomBrief}
          onDeleteSaved={onDeleteCustomBrief}
        />
      );
    case 'executive-summary':
      return (
        <ExecutiveSummaryPreferencesForm
          value={v}
          onChange={cb}
          siteReports={siteReports}
        />
      );
    // D17 — `financial` used to fall through to the default branch, so the
    // wizard and the rerun panel both rendered "No preferences form for type
    // financial" for a type with four declared preferences.
    case 'financial':
      return <FinancialPreferencesForm value={v} onChange={cb} />;
    default:
      return (
        <div className="text-sm text-text-muted">
          No preferences form for type "{reportType as string}".
        </div>
      );
  }
}

export type { ValidationResult };
// Type-keyed validator dispatch — pure, lives in lib for server use.
export { validatePreferencesFor } from '../../../lib/reportPreferences/validation';
